// src/lib/dates.ts
type FirestoreTimestampLike =
  | { seconds: number; nanoseconds?: number }
  | { toDate: () => Date };

export type DateInput = Date | string | number | null | undefined | FirestoreTimestampLike;

export function toDateSafe(input: DateInput): Date | null {
  if (!input) return null;

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
    }

  if (typeof input === 'number') {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof input === 'object') {
    // Firestore Timestamp object (client SDK)
    if ('toDate' in input && typeof (input as any).toDate === 'function') {
      const d = (input as any).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }

    // Timestamp-like {seconds}
    if ('seconds' in input && typeof (input as any).seconds === 'number') {
      const d = new Date((input as any).seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

export function formatDateTime(input: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDateSafe(input);
  if (!d) return 'TBD';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...opts,
  });
}

export function formatDateOnly(input: DateInput): string {
  const d = toDateSafe(input);
  if (!d) return 'TBD';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function formatExactDateTime(input: DateInput): string {
  const d = toDateSafe(input);
  if (!d) return 'TBD';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function sameLocalDate(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function formatCompactDateTimeRange(start: DateInput, end: DateInput): string {
  const s = toDateSafe(start);
  const e = toDateSafe(end);
  if (!s || !e) return `${formatDateTime(start)} - ${formatDateTime(end)}`;

  const sDate = s.toLocaleDateString();
  const sTime = s.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const eDate = e.toLocaleDateString();
  const eTime = e.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (sameLocalDate(s, e)) {
    return `${sDate}, ${sTime} - ${eTime}`;
  }
  return `${sDate}, ${sTime} - ${eDate}, ${eTime}`;
}

function plural(value: number, unit: string) {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

export function formatRelativeDateTime(input: DateInput, nowMs: number = Date.now()): string {
  const d = toDateSafe(input);
  if (!d) return 'TBD';

  const diffMs = Math.max(0, nowMs - d.getTime());
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (diffMs < minute) return 'just now';
  if (diffMs < hour) return plural(Math.floor(diffMs / minute), 'minute');
  if (diffMs < day) return plural(Math.floor(diffMs / hour), 'hour');
  if (diffMs < week) return plural(Math.floor(diffMs / day), 'day');
  if (diffMs < month) return plural(Math.floor(diffMs / week), 'week');
  if (diffMs < year) return plural(Math.floor(diffMs / month), 'month');
  return plural(Math.floor(diffMs / year), 'year');
}
